import { Injectable } from '@angular/core';
import { ActionsToolbarService } from '../actions-toolbar.service';
import { concat, forkJoin, Observable } from 'rxjs';
import { toArray } from 'rxjs/operators';
import { FloorsToUpdate, FloorUpdateAction, ReqFloorInfo, ReqFloorPlan } from '../actions-toolbar.model';
import { EDPFloorSpaceInfo, updateFloorEdpReq } from '../../floor-container/floor/floor.model';
import { FloorClass } from '../../../models/floor.class';
import { StackPlanViewStateService } from '../../../state/sp-view-state.service';
import { StackingPlanService } from '../../../stacking-plan.service';
import { COUNTRY_MAP } from '../../../shared/constants/countries.const';

@Injectable({ providedIn: 'root' })
export class FloorDataChangesHelperService {
  constructor(
    private actionsToolbarService: ActionsToolbarService,
    private spStateService: StackPlanViewStateService,
    private stackingPlanService: StackingPlanService
  ) {}

  private get propertyId() {
    return this.spStateService.getState().id;
  }

  /** Only US users use the EDP bulk updatePropertySpace API; the backend re-checks the country. */
  private isUSUser(): boolean {
    return (COUNTRY_MAP['United States of America'] ?? []).includes(
      this.stackingPlanService.resolveUser()?.userCountry
    );
  }

  getUpdatedFloorData(updatedFloorData, floorInfo) {
    const requestsToBeMade: Array<Observable<any>> = [];

    if (updatedFloorData != null) {
      const floorsChanges = this.getUpdatedFloorInfo(updatedFloorData, floorInfo);
      const deletedFloorPlanInfo = this.getDeletedFloorPlanInfo(updatedFloorData, floorInfo);

      if (!!floorsChanges.length) {
        requestsToBeMade.push(this.processFloorsToUpdate(floorsChanges, floorInfo));
      }
      if (!!deletedFloorPlanInfo.length) {
        requestsToBeMade.push(this.deleteFloorsPlan(deletedFloorPlanInfo));
      }
    }

    return requestsToBeMade;
  }

  private getUpdatedFloors = (floorsWithPossibleUpdates: EDPFloorSpaceInfo[], originalFloorInfo: FloorClass[]) => {
    return floorsWithPossibleUpdates.filter(floorUpdated => {
      const currentFloorId = floorUpdated.floor_id;
      if (currentFloorId == null || currentFloorId < 0) {
        return false;
      } else {
        const floorToCompareWith = originalFloorInfo.find(
          comparableFloor => comparableFloor.floorId === currentFloorId
        );
        return (
          floorUpdated.floor_index !== floorToCompareWith.floorIndex ||
          floorUpdated.floor_desc !== floorToCompareWith.floorDesc ||
          floorUpdated.floor_number !== floorToCompareWith.floorNumber ||
          floorUpdated.floor_plan_url !== floorToCompareWith.floorImageURL ||
          floorUpdated.floor_size !== floorToCompareWith.floorArea ||
          floorUpdated.floor_size_uom_desc !== floorToCompareWith.floorAreaUOM ||
          floorUpdated.floor_slab_height !== floorToCompareWith.floorHeight ||
          floorUpdated.floor_slab_height_uom !== floorToCompareWith.floorHeightUOM ||
          floorUpdated.total_gross_area !== floorToCompareWith.totalGrossArea ||
          floorUpdated.total_gross_area_uom_desc !== floorToCompareWith.totalGrossAreaUOM ||
          floorUpdated.hide_floor_f !== floorToCompareWith.hideFloor
        );
      }
    });
  };

  private getFloorsToCreate = (floorsWithPossibleUpdates: EDPFloorSpaceInfo[]) => {
    return floorsWithPossibleUpdates.filter(newFloor => {
      const currentFloorId = newFloor.floor_id;
      return currentFloorId != null && currentFloorId < 0;
    });
  };

  /*
   * Checks for whether user has updated any floors or not
   */
  private getUpdatedFloorInfo(updatedFloorData: EDPFloorSpaceInfo[], floorInfo: FloorClass[]): EDPFloorSpaceInfo[] {
    return [...this.getUpdatedFloors(updatedFloorData, floorInfo), ...this.getFloorsToCreate(updatedFloorData)];
  }

  /*
   * Checks for whether user has deleted any floor plan or not
   */
  private getDeletedFloorPlanInfo(updatedFloorData: EDPFloorSpaceInfo[], floorInfo: FloorClass[]): EDPFloorSpaceInfo[] {
    return updatedFloorData.filter(floor => {
      const floorToCompareWith = floorInfo.find(comparableFloor => comparableFloor.floorIndex === floor.floor_index);
      return floorToCompareWith && floorToCompareWith.floorImageURL && !floor.floor_plan_url;
    });
  }

  private processFloorsToUpdate(floorsChanges: EDPFloorSpaceInfo[], currentFloorsInfo: FloorClass[]): Observable<any> {
    const cleanseUpdatableFloorData = this.cleanseUpdatableFloorData(floorsChanges, currentFloorsInfo);
    const floorsToUpdate = this.groupFloorsToUpdateByAction(cleanseUpdatableFloorData);
    const floorsUpdateRequests$ = this.generateFloorUpdateRequests(floorsToUpdate);
    // US requests run one after another: parallel writes to the same property are what make EDP's OpenSearch update fail.
    // Errors are handled per request so a failed bulk call does not drop the independent create and floor-number requests.
    const handleError = () => this.actionsToolbarService.errorHandling('errorMessages.updateFloorsInfo');
    return this.isUSUser()
      ? concat(...floorsUpdateRequests$.map(request$ => request$.pipe(handleError()))).pipe(toArray())
      : forkJoin(floorsUpdateRequests$).pipe(handleError());
  }

  /**
   * User deleted floor's floor plan info will transform into required format
   * and will send data through API to update the changes
   */
  private deleteFloorsPlan(deletedFloorPlanInfo: EDPFloorSpaceInfo[]): Observable<any> {
    const deleteFloorPlans = deletedFloorPlanInfo.map(
      ({ floor_plan_id, floor_id }) => new ReqFloorPlan(this.propertyId, floor_plan_id, floor_id)
    );
    return this.actionsToolbarService
      .deleteFloorPlan(deleteFloorPlans)
      .pipe(this.actionsToolbarService.errorHandling('errorMessages.deleteFloorPlan'));
  }

  private cleanseUpdatableFloorData(
    floorsChanges: EDPFloorSpaceInfo[],
    currentFloorsInfo: FloorClass[]
  ): EDPFloorSpaceInfo[] {
    const cleansedUpdatableFloors: EDPFloorSpaceInfo[] = [];

    floorsChanges.forEach(floor => {
      let result: EDPFloorSpaceInfo & { original_index?: number } = { ...floor };
      const currentFloorToUpdate = currentFloorsInfo.find(currentFloor => currentFloor.floorId === floor.floor_id);
      if (currentFloorToUpdate != null && currentFloorToUpdate.floorId >= 0) {
        let validIndexFloor: number = floor.floor_index;
        if (!FloorClass.isFloorIndexValid(floor.floor_index)) {
          const floorNumberAsNumber = Number(floor.floor_number);
          validIndexFloor = Number.isNaN(floorNumberAsNumber) ? floor.floor_index : floorNumberAsNumber;
        }
        const currentAsEdp = currentFloorToUpdate.toEDPFloorSpaceInfo();
        const difference = (obj1, obj2) =>
          Object.keys(obj1).filter(k => obj1[k] !== obj2[k] && !Array.isArray(obj1[k]) && !Array.isArray(obj2[k]));
        const dataToAdd = {};

        const diffKeys = difference(currentAsEdp, floor);
        diffKeys.forEach(key => (dataToAdd[key] = floor[key]));

        result = {
          floor_id: floor.floor_id,
          floor_index: validIndexFloor,
          ...dataToAdd,
        };
        if (floor.floor_index !== currentFloorToUpdate.floorIndex) {
          /*
           * include new field so groupFloorsToUpdateByAction can detect the
           * change on the index
           */
          result.original_index = currentFloorToUpdate.floorIndex;
        }
      } else {
        // removing aggregated negative floor id
        result.floor_id = null;
      }
      cleansedUpdatableFloors.push(result);
    });
    return cleansedUpdatableFloors;
  }

  private groupFloorsToUpdateByAction(floorsToUpdate: EDPFloorSpaceInfo[]): FloorsToUpdate {
    const withFloorNumbersToUpdate: EDPFloorSpaceInfo[] = [];
    const withFloorInfoToUpdateOrCreate: EDPFloorSpaceInfo[] = [];
    const withFloorNumberAndFloorInfoToUpdate: EDPFloorSpaceInfo[] = [];

    floorsToUpdate.forEach(floor => {
      const floorValuesWithNoNumberAndID = Object.entries(floor).filter(
        ([key]) => !['floor_number', 'floor_id', 'floor_index'].includes(key)
      );

      if (floor.floor_id == null || floor.floor_number == null) {
        withFloorInfoToUpdateOrCreate.push(floor);
      } else if (floor.floor_number != null && floorValuesWithNoNumberAndID.every(([, value]) => value == null)) {
        withFloorNumbersToUpdate.push(floor);
      } else if (floor.floor_number != null) {
        withFloorNumberAndFloorInfoToUpdate.push(floor);
      }
    });

    return {
      withFloorInfoToUpdateOrCreate,
      withFloorNumbersToUpdate,
      withFloorNumberAndFloorInfoToUpdate,
    };
  }

  /*
   * User updated floors info will transform into required format and will send data through API to update the changes
   */
  private generateFloorUpdateRequests(floorsToUpdate: FloorsToUpdate): Array<Observable<updateFloorEdpReq>> {
    const {
      withFloorInfoToUpdateOrCreate, // addPropertySpace (with or without index)
      withFloorNumbersToUpdate, // updateFloorNumber
      withFloorNumberAndFloorInfoToUpdate, // addPropertySpace & updateFloorNumber
    } = floorsToUpdate;
    let floorsToUpdateWithActions: [EDPFloorSpaceInfo[], FloorUpdateAction][] = [
      [withFloorInfoToUpdateOrCreate, 'floor-info'],
      [withFloorNumbersToUpdate, 'floor-number'],
      [withFloorNumberAndFloorInfoToUpdate, 'floor-number'],
    ];
    if (this.isUSUser()) {
      // Existing floors without a floor_number change go in one updatePropertySpace call; creates and number changes keep the legacy calls.
      const bulkFloors: EDPFloorSpaceInfo[] = [];
      const legacyFloors: EDPFloorSpaceInfo[] = [];
      withFloorInfoToUpdateOrCreate.forEach(floor =>
        (floor.floor_id != null && floor.floor_id > 0 && !('floor_number' in floor) ? bulkFloors : legacyFloors).push(
          floor
        )
      );
      floorsToUpdateWithActions = [
        [bulkFloors, 'floor-info-bulk'],
        [legacyFloors, 'floor-info'],
        [withFloorNumbersToUpdate, 'floor-number'],
        [withFloorNumberAndFloorInfoToUpdate, 'floor-number'],
      ];
    }
    return floorsToUpdateWithActions.map<Observable<updateFloorEdpReq>>(([floors, floorUpdateAction]) => {
      const floorsPayload: ReqFloorInfo[] = floors.map<ReqFloorInfo>(
        floor => new ReqFloorInfo(this.propertyId, floor, floorUpdateAction)
      );
      return this.actionsToolbarService.updateFloorsInfo(floorsPayload, floorUpdateAction);
    });
  }
}
